import { useNavigate } from 'react-router-dom'
import { Button } from 'antd'
import { ArrowRight, Sparkles } from 'lucide-react'
import styled from 'styled-components'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT, COLORS } from '../../styles/theme'

export interface BackToMindmapCTAProps {
  mindmapId: string
  /** Short headline shown above the description. */
  title?: string
  /** Body copy explaining what the user will see when they go back. */
  description?: string
  /** Button label. */
  buttonLabel?: string
}

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.lg};
  margin-top: ${SPACING.xl};
  padding: ${SPACING.xl};
  background: linear-gradient(135deg, ${COLORS.primary}10 0%, ${COLORS.purple500}08 100%);
  border: 1px solid ${COLORS.primary}33;
  border-radius: ${BORDER_RADIUS.lg};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }

  @media print {
    display: none;
  }
`

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${BORDER_RADIUS.full};
  background: ${COLORS.primary}1f;
  color: ${COLORS.primary};
  flex-shrink: 0;
`

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.lg};
  flex: 1;
  min-width: 0;
`

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Title = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
`

const Description = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.5;
`

export function BackToMindmapCTA({
  mindmapId,
  title = 'See the impact on your mindmap',
  description = "We've updated the confidence scores on your nodes based on this analysis. Head back to explore what's changed.",
  buttonLabel = 'Back to mindmap',
}: BackToMindmapCTAProps) {
  const navigate = useNavigate()

  return (
    <Card>
      <Content>
        <IconWrap>
          <Sparkles size={18} />
        </IconWrap>
        <TextWrap>
          <Title>{title}</Title>
          <Description>{description}</Description>
        </TextWrap>
      </Content>
      <Button
        type="primary"
        icon={<ArrowRight size={15} />}
        iconPosition="end"
        onClick={() => void navigate(`/mindmap/${mindmapId}`)}
      >
        {buttonLabel}
      </Button>
    </Card>
  )
}
